import { WarningIcon } from "@phosphor-icons/react";
import { isAxiosError } from "axios";
import { useIntl, type IntlShape } from "react-intl";

import { ReservationType } from "@/features/bookings/types/types";
import { type ApiError } from "@/types/types";

import PageError, { type PageErrorProps } from "./PageError";

interface Props {
  type: "flights" | "hotels";
  error: unknown;
}

const getErrorPageContent = ({
  type,
  error,
  intl,
}: Props & { intl: IntlShape }) => {
  let title =
    type === "flights"
      ? intl.formatMessage({
          id: "flights.errorWhileBooking",
          defaultMessage: "Error while booking the ticket",
        })
      : intl.formatMessage({
          id: "hotels.errorWhileBooking",
          defaultMessage: "An error occurred while booking the hotel",
        });
  let subtitles =
    type === "flights"
      ? [
          intl.formatMessage({
            id: "flights.errorWhileBookingDesc",
            defaultMessage:
              "Error while booking the ticket, please contact support team or try again later",
          }),
        ]
      : [
          intl.formatMessage({
            id: "hotels.errorWhileBookingDesc",
            defaultMessage:
              "An error occurred while booking your hotel, please contact our support team or try again later",
          }),
        ];
  let back: PageErrorProps["back"] = {
    text: intl.formatMessage({
      id: "backToHome",
      defaultMessage: "Back to home",
    }),
    options: { to: type === "flights" ? "/flights/search" : "/hotels/search" },
  };

  if (!(error instanceof Error)) {
    title = intl.formatMessage({
      id: "paymentError",
      defaultMessage: "Payment couldn't be completed",
    });
    subtitles = [
      intl.formatMessage({
        id: "paymentErrorDesc",
        defaultMessage:
          "Please check your QI account and try again the purchase later",
      }),
    ];
  }

  if (
    isAxiosError<ApiError>(error) &&
    error.response?.data.code === "VALIDATION_FAILED"
  ) {
    subtitles = [];
    back = { text: intl.formatMessage({ id: "back", defaultMessage: "Back" }) };
    error.response?.data.data.forEach((validationError) => {
      subtitles.push(validationError.message);
    });
  }

  return { title, subtitles, back };
};

export const BookingError = ({ error, type }: Props) => {
  const intl = useIntl();

  const { title, subtitles, back } = getErrorPageContent({ error, type, intl });

  return (
    <PageError
      title={title}
      subtitles={subtitles}
      back={{ ...back }}
      link={{
        text: intl.formatMessage({
          id: "orderDetails",
          defaultMessage: "Order details",
        }),
        options: {
          to: "/bookings",
          search: { category: type, reservationType: ReservationType.REFUNDED },
        },
      }}
      icon={<WarningIcon size={60} color="#de370c" />}
    />
  );
};
